import React, { Suspense, useEffect, useState } from 'react';
import { DrawerResizeHandle, useDrawerResize } from '@/components/DrawerResize';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import Close from '@mui/icons-material/Close';
import History from '@mui/icons-material/History';
import Refresh from '@mui/icons-material/Refresh';
import TimelineIcon from '@mui/icons-material/Timeline';
import { useBlocker, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useHasPermission } from '@/components/PermissionGate';
import { useSaveShortcut } from '@/hooks/useSaveShortcut';

interface ViewToolbarProps {
  /** Show back arrow button on the left */
  showBackButton?: boolean;
  /** Custom back handler (defaults to navigate(-1)) */
  onBack?: () => void;
  /** Children are rendered after the back button - use for action buttons */
  children?: React.ReactNode;
  /** Rendered right-aligned, before the history/timeline/refresh icons - use for icon actions */
  rightActions?: React.ReactNode;
  /** Handler called when refresh is clicked (and confirmed if there are unsaved changes) */
  onRefresh?: () => void;
  /** When true, a confirmation dialog will be shown before refresh proceeds */
  hasUnsavedChanges?: boolean;
  /** When set, Cmd+S / Ctrl+S triggers this handler (the browser's Save-page dialog is suppressed while mounted). */
  onSave?: () => void | Promise<void>;
  /** Mirror of the Save button's disabled expression; blocks the shortcut from firing without unregistering it. */
  saveDisabled?: boolean;
  /** When set, Escape triggers this handler (mirrors the Cancel button). Open dialogs/popovers keep their own Escape. */
  onCancel?: () => void;
  /** Mirror of the Cancel button's disabled expression. */
  cancelDisabled?: boolean;
  /** Entity type for the timeline drawer (e.g. "Order"). When set, a timeline icon is shown. */
  timelineEntityType?: string;
  /** Entity ID for the timeline drawer. Required when timelineEntityType is set. */
  timelineEntityId?: string;
  /** Entity type for audit history (e.g. "Order"). When set, a history icon is shown. */
  auditEntityType?: string;
  /** Entity ID for audit history. Required when auditEntityType is set. */
  auditEntityId?: string;
  /** Module name for audit history API (e.g. "Sales"). */
  auditModule?: string;
  /** Permission required to view audit history for this entity (e.g. "Sales.Order.ViewHistory"). */
  auditViewHistoryPermission?: string;
}

/**
 * ViewToolbar - D365 F&O style toolbar for list and detail views
 *
 * Renders a horizontal toolbar with:
 * - Optional back button on the left
 * - Action buttons passed as children (left-aligned)
 * - Optional refresh button on the right (with unsaved changes confirmation)
 * - White background with subtle bottom border
 *
 * Usage in Detail View:
 * ```
 * <ViewToolbar onRefresh={handleRefresh} hasUnsavedChanges={isDirty}
 *              onSave={handleSave} saveDisabled={isSaving || !isDirty}>
 *   <ActionsMenu ... />
 *   <Button onClick={handleCancel}>Cancel</Button>
 *   <Button onClick={handleSave}>Save</Button>
 * </ViewToolbar>
 * ```
 *
 * Usage in List View:
 * ```
 * <ViewToolbar onRefresh={handleRefresh}>
 *   <Button startIcon={<Add />} onClick={handleNew}>New</Button>
 *   <Button startIcon={<Delete />}>Delete</Button>
 * </ViewToolbar>
 * ```
 */
const noop = () => {};

export const ViewToolbar: React.FC<ViewToolbarProps> = ({
  showBackButton = true,
  onBack,
  children,
  rightActions,
  onRefresh,
  hasUnsavedChanges,
  onSave,
  saveDisabled,
  onCancel,
  cancelDisabled,
  timelineEntityType,
  timelineEntityId,
  auditEntityType,
  auditEntityId,
  auditModule,
  auditViewHistoryPermission,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [refreshDialogOpen, setRefreshDialogOpen] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const {
    width: timelineWidth,
    resizing: resizingTimeline,
    startResize: startTimelineResize,
  } = useDrawerResize({
    storageKey: 'nexo.processTimeline.width',
    defaultWidth: 480,
    minWidth: 420,
  });
  const [auditHistoryOpen, setAuditHistoryOpen] = useState(false);
  const canViewChangeHistory = useHasPermission(auditViewHistoryPermission ?? '');

  // Page-level Cmd+S / Ctrl+S and Escape. The one-ViewToolbar-per-page invariant (see the
  // blocker comment below) makes this the single page entry on the shortcut stack; dialogs
  // and panels register their own entries and win while open.
  useSaveShortcut(onSave ?? noop, {
    enabled: !!onSave || !!onCancel,
    canSave: !!onSave && !saveDisabled,
    onCancel,
    canCancel: !cancelDisabled,
  });

  /**
   * The unsaved-changes guard for EVERY way out of a dirty view — the main menu, an in-app link, this
   * toolbar's own back button, and the browser's back/forward buttons. It used to hang off the back
   * button's click handler alone, so every other exit silently discarded the user's work.
   *
   * Requires a DATA router (see App.tsx): `useBlocker` reads `useDataRouterContext`, which only
   * RouterProvider supplies. Note React Router honours ONE blocker at a time ("A router only supports
   * one blocker at a time"), which holds here because exactly one ViewToolbar mounts per page — detail
   * views are route targets and are never nested, and child grids render no toolbar. Nesting two
   * toolbars would silently drop one guard.
   *
   * Same-path navigations (a param-less re-render, a query-string change) are not exits — don't block.
   *
   * Nor is the hop a detail view makes after creating a record (/entity/new -> /entity/{id}): the form is
   * still dirty there — server truth only re-seeds on the following refetch — but the work is already
   * persisted, so prompting to discard it is both wrong and unactionable. The navigating view marks that
   * one navigation with `skipUnsavedGuard`.
   */
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !!hasUnsavedChanges &&
      currentLocation.pathname !== nextLocation.pathname &&
      !(nextLocation.state as { skipUnsavedGuard?: boolean } | null)?.skipUnsavedGuard,
  );

  // Closing the tab / reloading is outside the router entirely, so the blocker cannot see it. This is
  // the only thing that catches it: a non-empty returnValue makes the browser show its own native
  // "Leave site?" prompt. Registered only while dirty — an always-on handler would nag on every exit.
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = ''; // required by Chrome to trigger the native prompt
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [hasUnsavedChanges]);

  // The back button just navigates — the blocker above raises the confirm dialog when dirty. Prompting
  // here as well would ask twice: once from the button, once from the blocker on the resulting navigate.
  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  const handleRefreshClick = () => {
    if (hasUnsavedChanges) {
      setRefreshDialogOpen(true);
    } else {
      onRefresh?.();
    }
  };

  const handleRefreshConfirm = () => {
    setRefreshDialogOpen(false);
    onRefresh?.();
  };

  const handleRefreshCancel = () => {
    setRefreshDialogOpen(false);
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          mx: 2,
          mt: 2,
          mb: 1,
          px: 2,
          py: 0.75,
          bgcolor: 'background.paper',
          borderRadius: 1,
          boxShadow: 1,
          minHeight: 48,
          flexShrink: 0,
        }}
      >
        {showBackButton && (
          <>
            <IconButton
              onClick={handleBack}
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': { color: 'text.primary' }
              }}
            >
              <ArrowBack />
            </IconButton>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 28, alignSelf: 'center' }} />
          </>
        )}

        {children}

        {/* Spacer to push any right-aligned content */}
        <Box sx={{ flexGrow: 1 }} />

        {rightActions}

        {canViewChangeHistory && auditEntityType && auditEntityId && (
          <Tooltip title={t('toolbar.changeHistory', { defaultValue: 'Change History' })}>
            <IconButton
              onClick={() => setAuditHistoryOpen(true)}
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': { color: 'text.primary' }
              }}
            >
              <History />
            </IconButton>
          </Tooltip>
        )}

        {timelineEntityType && timelineEntityId && (
          <Tooltip title={t('toolbar.processTimeline', { defaultValue: 'Process Timeline' })}>
            <IconButton
              onClick={() => setTimelineOpen(true)}
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': { color: 'text.primary' }
              }}
            >
              <TimelineIcon />
            </IconButton>
          </Tooltip>
        )}

        {onRefresh && (
          <Tooltip title={t('toolbar.refreshData')}>
            <IconButton
              onClick={handleRefreshClick}
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': { color: 'text.primary' }
              }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Dialog open={refreshDialogOpen} onClose={handleRefreshCancel}>
        <DialogTitle>{t('toolbar.unsavedChanges')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('toolbar.unsavedChangesMessage')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRefreshCancel}>{t('common.cancel')}</Button>
          <Button onClick={handleRefreshConfirm} color="primary" variant="contained">
            {t('toolbar.refresh')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Raised by the blocker for ANY exit from a dirty view (menu, link, back button, browser back).
          Discard lets the navigation through; Keep editing cancels it and leaves the user where they are. */}
      <Dialog open={blocker.state === 'blocked'} onClose={() => blocker.reset?.()}>
        <DialogTitle>{t('common.discardChanges', { defaultValue: 'Discard changes?' })}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('common.discardChangesMessage', { defaultValue: 'You have unsaved changes. Are you sure you want to discard them?' })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => blocker.reset?.()}>
            {t('common.keepEditing', { defaultValue: 'Keep editing' })}
          </Button>
          <Button onClick={() => blocker.proceed?.()} color="primary" variant="contained">
            {t('common.discard', { defaultValue: 'Discard' })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Audit History Drawer */}
      {canViewChangeHistory && auditEntityType && auditEntityId && (
        <Suspense fallback={null}>
          <AuditHistoryDrawerLazy
            open={auditHistoryOpen}
            onClose={() => setAuditHistoryOpen(false)}
            entityType={auditEntityType}
            module={auditModule}
            entityId={auditEntityId}
          />
        </Suspense>
      )}

      {/* Process Timeline Drawer */}
      {timelineEntityType && timelineEntityId && (
        <Drawer
          anchor="right"
          open={timelineOpen}
          onClose={() => setTimelineOpen(false)}
          PaperProps={{ sx: { width: { xs: '100%', sm: timelineWidth }, maxWidth: '100vw' } }}
        >
          <DrawerResizeHandle
            onMouseDown={startTimelineResize}
            resizing={resizingTimeline}
            label={t('toolbar.resizePanel', { defaultValue: 'Resize panel' })}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" fontWeight={600}>
              {t('toolbar.processTimeline', { defaultValue: 'Process Timeline' })}
            </Typography>
            <IconButton onClick={() => setTimelineOpen(false)} size="small">
              <Close />
            </IconButton>
          </Box>
          <Box sx={{ overflow: 'auto', flex: 1 }}>
            <Suspense fallback={<Box sx={{ p: 3, textAlign: 'center' }}><Typography color="text.secondary">Loading timeline...</Typography></Box>}>
              <EntityTimelineLazy entityType={timelineEntityType} entityId={timelineEntityId} />
            </Suspense>
          </Box>
        </Drawer>
      )}
    </>
  );
};

/** Lazy-load the EntityTimeline component to avoid pulling it into every page bundle */
const EntityTimelineLazy = React.lazy(() =>
  import('@/components/EntityTimeline').then(m => ({ default: m.EntityTimeline }))
);

/** Lazy-load the AuditHistoryDrawer to avoid pulling it into every page bundle */
const AuditHistoryDrawerLazy = React.lazy(() =>
  import('@/components/AuditHistoryDrawer').then(m => ({ default: m.AuditHistoryDrawer }))
);

export default ViewToolbar;
